import { stringify } from 'qs';

// interface
import type { ApiDict } from '@/interface';

import { LogModule } from '@/data/config';
import { RedirectType } from '@/data/url';
import { isBrowser } from '@/modules/detector';
import { commonJump, getBrowser } from '@/modules/utils';
import { isArray } from '@/modules/validate';
import { loggerError } from '@/modules/logger';

import { getGenericConfig } from '@/modules/url/config';
import { ENV, getEnv, getParseUrl } from './common';

// 跳转目标
enum TargetType {
  ToVbkr = 'vbkr',
  ToHstong = 'hstong',
}

/**
 * 获取passport域名
 * @returns
 */
function getPassportDomain(target: TargetType, env?: ENV): string {
  const _env = env ?? getEnv();

  if (target === TargetType.ToVbkr) {
    return `https://passport${_env === ENV.Prod ? '' : _env}.hstong.com/jump/hstong-to-vbkr`;
  }

  return isBrowser() ? getBrowser().location.origin + '/passport/jump/to-hstong' : '';
}

interface IJumpDomainOptions {
  target?: TargetType;
  targetKey?: string; // 根据后台配置 HSTONG_TO_VBKR，目前写死 www.vbkr.com
  env?: ENV;
  redirect?: RedirectType;
  isNeedEncode?: boolean;
  query?: ICommonObject;
}
/**
 * 客户端
 * 获取通用配置跳转
 * vbkr 跳转到 hstong 域名
 * hstong 跳转到 vbkr 域名
 * @param {string} options[pathname] (必填) 自定义传入结果路径
 * @param {string} options[target] （可选） 跳转的目标域名 vbkr|hstong 默认是 hstong 跳转到 vbkr 域名
 * @param {string} options[targetKey] （可选）根据后台配置 HSTONG_TO_VBKR
 * @param {string} options[env] （可选）当前配置环境，默认取当前链接，来区分环境
 * @param {string} options[redirect] （可选）是否需要自动跳转 默认不跳转 href|replace
 * @param {string} options[query] （可选）路径地址参数
 */
export function getJumpDomainCustom(
  options: {
    pathname: string;
    query?: ICommonObject;
  } & IJumpDomainOptions
) {
  const _options = {
    target: TargetType.ToVbkr,
    isNeedEncode: true,
    redirect: '',
    ...(options ?? {}),
  };

  if (!options.pathname) {
    loggerError('getJumpDomainCustom 方法的 或 pathname 参数必填', LogModule.Url);
    return '';
  }

  // https://passportdaily.hstong.com/jump/hstong-to-vbkr?targetKey=targetKey&targetPath=targetPath
  // https://www.vbkr.com/passport/jump/to-hstong?target=https://khdaily.hstong.com/apply/app-switch

  // 跳转地址
  const passportDomain = getPassportDomain(_options.target, _options?.env);

  // 判断query 存不存在
  const query = _options.query
    ? `${/\?/.test(_options.pathname) ? '&' : '?'}` + stringify(_options.query)
    : '';

  const _pathname = _options.pathname + query;

  let result = `${passportDomain}?targetKey=${_options.targetKey ?? 'www.vbkr.com'}&targetPath=${
    _options.isNeedEncode ? encodeURIComponent(_pathname) : _pathname
  }`;

  // 判断跳转到 hstong.com 域名情况
  if (_options.target === TargetType.ToHstong) {
    const _target = `${
      isBrowser()
        ? getBrowser().location.origin.replace(/(https?:\/\/)(.+?\.)(.+)/i, '$1$2hstong.com')
        : ''
    }${_pathname}`;
    result = `${passportDomain}?target=${
      _options.isNeedEncode ? encodeURIComponent(_target) : _target
    }`;
  }

  commonJump({ url: result, redirect: _options?.redirect as RedirectType });

  return result;
}

// getJumpDomain({target: 'vbkr', keys: 'openAcAShares', targetKey:'baidu.com'})

/**
 * 客户端
 * 获取通用配置跳转
 * vbkr 跳转到 hstong 域名
 * hstong 跳转到 vbkr 域名
 * @param {string} options[keys] (必填) 需要获取后台配置对应的H5链接
 * @param {string} options[target] （可选） 跳转的目标域名 vbkr|hstong 默认是 hstong 跳转到 vbkr 域名
 * @param {string} options[targetKey] （可选）根据后台配置 HSTONG_TO_VBKR
 * @param {string} options[env] （可选）当前配置环境，默认取当前链接，来区分环境
 * @param {string} options[redirect] （可选）是否需要自动跳转 默认不跳转 href|replace
 */
export async function getJumpDomain(
  options: {
    keys: string | { key: string; query: Record<string, any> }[];
  } & IJumpDomainOptions
) {
  const _options = {
    target: TargetType.ToVbkr,
    redirect: '',
    ...(options ?? {}),
  };

  if (!_options.target || !_options.keys) {
    loggerError('getJumpDomain方法的 target, 或 key 参数必填', LogModule.Url);
    return;
  }

  // 判断keys 是否 数组
  const keysParams = isArray(options.keys) ? options.keys.map(i => i.key).join(',') : options.keys;

  const [[err, response]] = await getGenericConfig({
    urlsParams: {
      keys: keysParams,
    },
  });

  if (err || !response.success || response.data.length === 0) {
    loggerError('接口异常, 或者keys有问题', LogModule.Url);
    // 接口异常情况处理，默认返回
    return {
      ...response,
      options,
    };
  }

  // https://passportdaily.hstong.com/jump/hstong-to-vbkr?targetKey=targetKey&targetPath=targetPath
  // https://www.vbkr.com/passport/jump/to-hstong?target=https://khdaily.hstong.com/apply/app-switch

  // 格式化数据
  const dataArr = response.data.map((i: ApiDict.ISingleValue, index: number) => {
    const data = getParseUrl(i.value as string);

    // data不存在情况
    if (!data) {
      return {
        ...i,
        url: '',
      };
    }

    let _query;
    if (isArray(_options.keys)) {
      _query = _options.keys[index]?.query;
    }

    const url = getJumpDomainCustom({
      pathname: data.pathname,
      target: _options.target,
      targetKey: _options?.targetKey,
      env: _options.env,
      isNeedEncode: _options.isNeedEncode,
      query: _query,
    });

    return {
      ...i,
      url,
    };
  });

  commonJump({ url: dataArr[0].url, redirect: _options?.redirect as RedirectType });

  return dataArr;
}
